import { useState, useEffect, Suspense } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { BarChart3, Grid3X3, List, MapPin, RefreshCw, Menu, LogOut, Check, Settings, ChevronDown, Sparkles, Star, Image, GlassWater, MoreHorizontal } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/contexts/useAuth'
import { useCellarContext } from '@/contexts/useCellarContext'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'

const primaryNavItems = [
  { path: '/', label: 'Cellar', icon: Grid3X3 },
  { path: '/bottles', label: 'Bottles', icon: List },
  { path: '/analytics', label: 'Analytics', icon: BarChart3 },
  { path: '/map', label: 'Map', icon: MapPin },
  { path: '/sync', label: 'Sync', icon: RefreshCw },
]

const secondaryNavItems = [
  { path: '/story', label: 'Story', icon: Sparkles },
  { path: '/wrapped', label: 'Wrapped', icon: Star },
  { path: '/showcases', label: 'Showcase', icon: Image },
  { path: '/tastings', label: 'Tastings', icon: GlassWater },
]

const allNavItems = [...primaryNavItems, ...secondaryNavItems]

function getUserInitials(email: string) {
  const name = email.split('@')[0]
  const parts = name.split(/[._-]/)
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase()
  }
  return name.slice(0, 2).toUpperCase()
}

function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])
  return null
}

export function Layout() {
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const { user, signOut } = useAuth()
  const { cellars, cellarId, cellarName, selectCellar } = useCellarContext()

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="border-b border-border">
        <div className="mx-auto flex h-16 max-w-[1200px] items-center justify-between px-4 sm:px-6">
          <Link to="/" className="flex shrink-0 items-center gap-2.5">
            <img src="/cellarwise_icon.png" alt="" className="h-8 w-8 rounded-lg" />
            <span className="hidden font-heading text-xl font-semibold tracking-wide text-foreground sm:inline">
              CellarWise
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden items-center gap-1 md:flex">
            {/* Cellar switcher */}
            {cellars.length > 1 ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="mr-2 gap-1 text-sm font-medium text-muted-foreground">
                    {cellarName}
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <DropdownMenuLabel>Cellars</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {cellars.map((c) => (
                    <DropdownMenuItem key={c.id} onClick={() => selectCellar(c.id)}>
                      {c.id === cellarId && <Check className="mr-2 h-4 w-4 text-primary" />}
                      <span className={c.id === cellarId ? 'font-medium' : ''}>{c.name}</span>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/cellars" className="flex items-center gap-2">
                      <Settings className="h-4 w-4" />
                      Manage Cellars
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : cellars.length === 1 ? (
              <span className="mr-2 text-sm font-medium text-muted-foreground">{cellarName}</span>
            ) : null}

            <nav className="flex items-center gap-0.5 lg:gap-1">
              {primaryNavItems.map(({ path, label, icon: Icon }) => (
                <Link
                  key={path}
                  to={path}
                  title={label}
                  className={cn(
                    'flex items-center gap-2 rounded-lg px-2 py-2 font-heading uppercase tracking-[2px] text-[13px] font-medium transition-colors lg:px-3',
                    (pathname === path || (path !== '/' && pathname.startsWith(path)))
                      ? 'bg-secondary text-primary'
                      : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                  )}
                >
                  <Icon className="h-4 w-4" strokeWidth={1.5} />
                  <span className="hidden lg:inline">{label}</span>
                </Link>
              ))}

              {/* "More" dropdown for secondary items */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    title="More"
                    className={cn(
                      'flex items-center gap-2 rounded-lg px-2 py-2 font-heading uppercase tracking-[2px] text-[13px] font-medium transition-colors lg:px-3',
                      secondaryNavItems.some(({ path }) => pathname === path || pathname.startsWith(path + '/'))
                        ? 'bg-secondary text-primary'
                        : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                    )}
                  >
                    <MoreHorizontal className="h-4 w-4" strokeWidth={1.5} />
                    <span className="hidden lg:inline">More</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {secondaryNavItems.map(({ path, label, icon: Icon }) => (
                    <DropdownMenuItem key={path} asChild>
                      <Link
                        to={path}
                        className={cn(
                          'flex items-center gap-2',
                          (pathname === path || pathname.startsWith(path + '/'))
                            ? 'font-semibold text-primary'
                            : ''
                        )}
                      >
                        <Icon className="h-4 w-4" strokeWidth={1.5} />
                        {label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>

            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="ml-2 h-8 w-8 rounded-full bg-primary p-0 text-xs font-medium text-primary-foreground hover:bg-primary/90"
                  >
                    {getUserInitials(user.email ?? '')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="font-normal">
                    <p className="truncate text-sm text-muted-foreground">
                      {user.email}
                    </p>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/settings">
                      <Settings className="mr-2 h-4 w-4" />
                      Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => signOut()}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Mobile hamburger */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon">
                <Menu className="h-5 w-5" strokeWidth={1.5} />
                <span className="sr-only">Open menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-64">
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2.5 font-heading">
                  <img src="/cellarwise_icon.png" alt="" className="h-6 w-6 rounded-md" />
                  CellarWise
                </SheetTitle>
              </SheetHeader>
              {/* Mobile cellar switcher */}
              {cellars.length > 1 && (
                <div className="mt-4 space-y-1">
                  <p className="px-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Cellar</p>
                  {cellars.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => { selectCellar(c.id); setOpen(false) }}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors',
                        c.id === cellarId ? 'bg-secondary font-medium text-primary' : 'text-muted-foreground hover:bg-secondary',
                      )}
                    >
                      {c.id === cellarId && <Check className="h-3 w-3" />}
                      {c.name}
                    </button>
                  ))}
                  <Link
                    to="/cellars"
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-secondary"
                  >
                    <Settings className="h-3 w-3" />
                    Manage Cellars
                  </Link>
                </div>
              )}

              <nav className="mt-6 flex flex-col gap-1">
                {allNavItems.map(({ path, label, icon: Icon }) => (
                  <Link
                    key={path}
                    to={path}
                    onClick={() => setOpen(false)}
                    className={cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2.5 font-heading uppercase tracking-[2px] text-[13px] font-medium transition-colors',
                      (pathname === path || (path !== '/' && pathname.startsWith(path)))
                        ? 'bg-secondary text-primary'
                        : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                    )}
                  >
                    <Icon className="h-4 w-4" strokeWidth={1.5} />
                    {label}
                  </Link>
                ))}
              </nav>

              {user && (
                <div className="mt-auto border-t border-border pt-4">
                  <p className="truncate px-3 text-xs text-muted-foreground">
                    {user.email}
                  </p>
                  <Link
                    to="/settings"
                    onClick={() => setOpen(false)}
                    className="mt-2 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 font-heading uppercase tracking-[2px] text-[13px] font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                  >
                    <Settings className="h-4 w-4" strokeWidth={1.5} />
                    Settings
                  </Link>
                  <button
                    onClick={() => { setOpen(false); signOut() }}
                    className="mt-2 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 font-heading uppercase tracking-[2px] text-[13px] font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                  >
                    <LogOut className="h-4 w-4" strokeWidth={1.5} />
                    Sign out
                  </button>
                </div>
              )}
            </SheetContent>
          </Sheet>
        </div>
      </header>
      <ScrollToTop />
      <main className="mx-auto w-full max-w-[1200px] flex-1 px-4 py-6 sm:px-6 sm:py-12">
        <Suspense
          fallback={
            <div className="flex min-h-[50vh] items-center justify-center">
              <img src="/cellarwise_icon.png" alt="" className="h-10 w-10 animate-pulse rounded-lg" />
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </main>
      <footer className="border-t border-sidebar-border bg-sidebar">
        <div className="mx-auto flex flex-col items-center gap-4 max-w-[1200px] px-4 py-6 sm:flex-row sm:justify-between sm:px-6">
          <div className="flex items-center gap-2.5">
            <img src="/cellarwise_icon.png" alt="" className="h-6 w-6 rounded-md" />
            <span className="font-heading text-sm font-medium text-sidebar-foreground">CellarWise</span>
          </div>
          <nav className="flex items-center gap-4 text-sm text-sidebar-foreground/60">
            <Link to="/analytics" className="transition-colors hover:text-sidebar-foreground">Analytics</Link>
            <Link to="/settings" className="transition-colors hover:text-sidebar-foreground">Settings</Link>
          </nav>
          <p className="font-serif text-xs italic text-sidebar-foreground/40">
            &copy; {new Date().getFullYear()} CellarWise
          </p>
        </div>
      </footer>
    </div>
  )
}
